// SFU control-plane WebSocket client (experiment — SocketExperiment branch).
//
// Mirror of `video_conference/src/ws/*`. One persistent socket per peer to the
// assigned SFU node. Replaces the per-call `sfuFetch` REST control plane with:
//   - request/response RPC (`request(method, data)` → Promise) via id correlation
//   - server→client push notifications (`on(method, handler)`): newProducer,
//     producerPaused/Resumed/Closed — the reason for moving off REST.
//
// Protocol (JSON), matching the server:
//   request      client→SFU : { id, method, data }
//   response     SFU→client : { id, ok:true, data } | { id, ok:false, error }
//   notification SFU→client : { method, data }            (no id)

import { vclog } from './vc-log'
import { createLivenessMonitor, type LivenessMonitor } from '../services/signaling-liveness'

type Pending = {
  resolve: (data: unknown) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

type OpenWaiter = {
  resolve: () => void
  reject: (err: Error) => void
}

type NotificationHandler = (data: unknown) => void

/** How the socket closed, as the server said it. */
export type SfuSocketClose = { code: number; reason: string }

export type SfuSocketOptions = {
  /** SFU node base URL, e.g. "https://sfu1.instavc.ai" or "http://127.0.0.1:3001". */
  nodeUrl: string
  roomId: string
  peerId: string
  /** Shared SFU api key (mirrors the REST x-api-key). */
  key: string
  /** Called on every notification, after any specific `on()` handler. */
  onNotification?: (method: string, data: unknown) => void
  /** Called when the socket (re)connects and is ready for requests. */
  onOpen?: () => void
  /** Called when the socket closes (before a reconnect attempt). */
  onClose?: (close: SfuSocketClose) => void
  /**
   * Whether to reconnect after this close. Absent means always, which is what
   * the meeting wants. (A `replaced` close never reconnects, whatever this
   * says — see `handleReplaced`.) The Screen Share page uses it to stay
   * closed: its peerId is shared with any other share page of the same
   * account, and reconnecting would replace that page in turn.
   */
  shouldReconnect?: (close: SfuSocketClose) => boolean
  /**
   * Asked before each reconnect attempt, once its backoff has elapsed: true to
   * go ahead, false to stay closed for good. Absent means go ahead at once,
   * which is what the meeting wants.
   *
   * The Screen Share page holds its reconnect until its room channel says the
   * page still owns its share id. After a laptop sleeps, the page's sockets
   * come back by themselves; one that reached the SFU first would take the
   * peer from the page that opened since — before the room could tell this
   * one it had been replaced (contract §7b).
   */
  beforeReconnect?: () => Promise<boolean>
}

const REQUEST_TIMEOUT_MS = 15_000
const MAX_BACKOFF_MS = 30_000
const INITIAL_BACKOFF_MS = 500

/**
 * How long a request waits for the socket to be open before it fails.
 *
 * It used to wait for ever. A node that could not be reached left
 * `control.join()` pending — neither resolved nor rejected — so the join's own
 * retry never ran and the session sat at "connecting"; an ICE restart asked
 * for during an outage hung the watchdog that asked for it the same way.
 */
export const SFU_OPEN_TIMEOUT_MS = 15_000

/**
 * Liveness: a `ping` request every 20 s, which must be answered (or anything
 * else must arrive) within 10 s.
 *
 * A socket left behind by a network change stays OPEN with nothing getting
 * through — measured 2026-10-13: a black-holed control socket sat "open" for
 * over a minute, and would have for ~15, while every request on it timed out
 * and every push (a new participant's `newConsumer`) was lost. The browser
 * never fires `close` for it; only an unanswered request says so.
 *
 * The server has no `ping` method and does not need one: it answers an unknown
 * method with an error reply, which proves the socket just as well. The 10 s
 * deadline is deliberately lenient — under load a slow answer is not a dead
 * socket, and replacing the sockets of a busy node is how load becomes an
 * outage.
 *
 * The probe on the browser's `online` event gets the same 10 s, not the
 * signaling socket's 5 s. Right after a same-IP outage the old connection is
 * usually alive and only waiting out TCP's retransmit backoff; replacing it
 * then rejects requests the server may still apply (a first connectTransport,
 * a produce) and lets requests queued on it land after newer ones sent on the
 * replacement. And a socket that really is dead is caught by the signaling
 * socket's own 5 s probe first, whose failure rebuilds this whole session.
 */
export const SFU_PING_INTERVAL_MS = 20_000
export const SFU_PING_TIMEOUT_MS = 10_000
export const SFU_ONLINE_PROBE_TIMEOUT_MS = 10_000

/**
 * Pushes the server sends that this client knows about and deliberately does
 * not act on. Listed so they do not trip the "no handler" warning below, which
 * exists to catch pushes nobody was meant to miss.
 *
 * `producerScore` — the SFU's score for each of our own producers, sent on
 * every change. Nothing in the web client shows it yet.
 */
const KNOWN_UNHANDLED_NOTIFICATIONS = new Set(['producerScore'])

/**
 * The close the SFU sends a socket when another one registers for the same
 * room and peer (ws-server.js `register`). Both halves are matched: 4000 alone
 * is also what a test harness's injected close uses, and that is a drop to
 * reconnect from, not a takeover.
 */
export const SFU_REPLACED_CLOSE = { code: 4000, reason: 'replaced' } as const

export function isReplacedClose(ev?: { code?: number; reason?: string }): boolean {
  return ev?.code === SFU_REPLACED_CLOSE.code && ev?.reason === SFU_REPLACED_CLOSE.reason
}

/**
 * Build the ws(s):// URL for the node's /ws endpoint. `/ws` goes under the node
 * URL's path, not in place of it: through the SFU gateway that path is
 * `/node/<nodeId>`, and it is the only thing naming the node.
 */
function toWsUrl(nodeUrl: string, roomId: string, peerId: string, key: string): string {
  const u = new URL(nodeUrl)
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:'
  u.pathname = `${u.pathname.replace(/\/+$/, '')}/ws`
  u.search = `?roomId=${encodeURIComponent(roomId)}&peerId=${encodeURIComponent(peerId)}&key=${encodeURIComponent(key)}`
  return u.toString()
}

export class SfuSocket {
  private ws: WebSocket | null = null
  private nextId = 1
  private pending = new Map<number, Pending>()
  private handlers = new Map<string, Set<NotificationHandler>>()
  private opts: SfuSocketOptions
  private closedByUser = false
  private backoff = INITIAL_BACKOFF_MS
  private openWaiters: OpenWaiter[] = []
  private reconnectHandlers = new Set<() => void>()
  private replacedHandlers = new Set<() => void>()
  private hasConnectedOnce = false
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** The current socket's ping; null while it is not open. */
  private liveness: LivenessMonitor | null = null
  /** Give up on the attempt now connecting; null once it has opened or ended. */
  private abandonAttempt: (() => void) | null = null
  private readonly onOnline = () => this.handleOnline()

  constructor(opts: SfuSocketOptions) {
    this.opts = opts
    this.connect()
    // After connect(), which can throw (a URL it cannot parse, a socket the
    // page may not open): a listener added first would outlive an instance the
    // caller never received and so can never close().
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline)
  }

  /** Subscribe to a server notification (e.g. "newProducer"). Returns unsubscribe. */
  on(method: string, handler: NotificationHandler): () => void {
    let set = this.handlers.get(method)
    if (!set) { set = new Set(); this.handlers.set(method, set) }
    set.add(handler)
    return () => set!.delete(handler)
  }

  /** Fired on every socket (re)connect AFTER the first, so callers can re-sync
   *  server-side state that was lost while the socket was down (e.g. re-run
   *  readyToConsume so missed `newConsumer` pushes are recovered). */
  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler)
    return () => this.reconnectHandlers.delete(handler)
  }

  /**
   * Resolves once the socket is OPEN (immediately if already open). Rejects if
   * the socket is closed for good, or — given `timeoutMs` — if it is not open
   * by then.
   */
  ready(timeoutMs?: number): Promise<void> {
    if (this.closedByUser) return Promise.reject(new Error('socket closed'))
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return Promise.resolve()
    return new Promise((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined
      const waiter: OpenWaiter = {
        resolve: () => { clearTimeout(timer); resolve() },
        reject: (err) => { clearTimeout(timer); reject(err) },
      }
      if (timeoutMs !== undefined) {
        timer = setTimeout(() => {
          this.openWaiters = this.openWaiters.filter((w) => w !== waiter)
          reject(new Error(`SFU socket not open after ${timeoutMs} ms`))
        }, timeoutMs)
      }
      this.openWaiters.push(waiter)
    })
  }

  /** Send an RPC request and await its response. */
  async request<T = unknown>(method: string, data?: unknown): Promise<T> {
    try {
      await this.ready(SFU_OPEN_TIMEOUT_MS)
    } catch (err) {
      throw new Error(`${(err as Error).message}: ${method}`)
    }
    // close() in the same tick as this call: it rejected nothing of ours, as
    // we were not waiting yet, so say so here.
    if (this.closedByUser) throw new Error(`socket closed: ${method}`)
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('SFU socket not open')
    const id = this.nextId++
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        reject(new Error(`SFU request timed out: ${method}`))
      }, REQUEST_TIMEOUT_MS)
      this.pending.set(id, { resolve: resolve as (d: unknown) => void, reject, timer })
      ws.send(JSON.stringify({ id, method, data }))
    })
  }

  /**
   * Subscribe to this peer's socket being taken over by another one — the
   * same person's meeting in another tab or window. The socket stops for good
   * when it happens. Returns unsubscribe.
   */
  onReplaced(handler: () => void): () => void {
    this.replacedHandlers.add(handler)
    return () => this.replacedHandlers.delete(handler)
  }

  /**
   * The server closed us because another socket registered for this room and
   * peer. Reconnecting would take it back, that one would do the same, and
   * the two would replace each other about once a second for ever — each
   * cycle re-pushing the whole room and losing half the pushes to the other
   * tab (measured in simulation: 109 sockets in 61 s). So stop, and say so.
   */
  private handleReplaced(): void {
    vclog('socket', 'SFU control socket taken over by another tab or window — not reconnecting', {
      roomId: this.opts.roomId, peerId: this.opts.peerId,
    })
    this.close()
    this.replacedHandlers.forEach((h) => { try { h() } catch { /* ignore */ } })
  }

  close(): void {
    if (this.closedByUser) return
    this.closedByUser = true
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline)
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.abandonAttempt = null
    this.stopLiveness()
    this.rejectPending()
    // Nothing waiting for this socket to open will see it open now. They used
    // to wait for ever.
    const waiters = this.openWaiters
    this.openWaiters = []
    waiters.forEach((w) => w.reject(new Error('socket closed')))
    if (this.ws) { try { this.ws.close() } catch { /* ignore */ } }
    this.ws = null
  }

  // ── internals ─────────────────────────────────────────────────
  private connect(): void {
    this.reconnectTimer = null
    if (this.closedByUser) return
    const { nodeUrl, roomId, peerId, key } = this.opts
    const ws = new WebSocket(toWsUrl(nodeUrl, roomId, peerId, key))
    this.ws = ws
    let ended = false
    // An attempt whose handshake never completes has no deadline of its own:
    // a SYN lost in an outage is retried by the kernel for minutes, and Chrome
    // queues every later socket to the same host behind it. Bounded like a
    // request's wait for the socket.
    const openDeadline = setTimeout(() => {
      if (ws.readyState !== WebSocket.CONNECTING) return
      vclog('socket', 'SFU control socket did not open in time — replacing it', {
        roomId: this.opts.roomId, peerId: this.opts.peerId, waitedMs: SFU_OPEN_TIMEOUT_MS,
      })
      handleClosed('unresponsive')
    }, SFU_OPEN_TIMEOUT_MS)

    // One way out for this socket, however it ends: the browser's close, or a
    // ping that went unanswered. Runs once.
    const handleClosed = (why: 'close' | 'unresponsive', closeEvent?: { code?: number; reason?: string }) => {
      if (ended) return
      ended = true
      clearTimeout(openDeadline)
      if (this.abandonAttempt === abandon) this.abandonAttempt = null
      if (this.ws === ws) this.stopLiveness()
      if (why !== 'close') {
        // Do not wait for the browser to report it: on a socket whose network
        // path is gone the close handshake never completes, and `close` comes
        // only when the kernel gives up on the connection.
        ws.onopen = null
        ws.onmessage = null
        ws.onclose = null
        ws.onerror = null
        try { ws.close() } catch { /* already closing */ }
      }
      if (this.ws === ws) this.ws = null
      const close: SfuSocketClose = { code: closeEvent?.code ?? 0, reason: closeEvent?.reason ?? '' }
      this.opts.onClose?.(close)
      if (this.closedByUser) return
      if (why === 'close' && isReplacedClose(closeEvent)) {
        this.handleReplaced()
        return
      }
      if (this.opts.shouldReconnect && !this.opts.shouldReconnect(close)) {
        // Closed for good, as if by the caller.
        vclog('socket', 'closed — not reconnecting', { roomId: this.opts.roomId, peerId: this.opts.peerId, ...close })
        this.close()
        return
      }
      // Reconnect with exponential backoff; pending requests fail and retry
      // is left to the caller (idempotent setup calls can be re-issued).
      this.rejectPending()
      // Jittered between half and all of the step, as the signaling socket's
      // is: when a node stalls, every client's socket goes together, and
      // without it they would all come back — and each ask the node to re-push
      // its whole room — together.
      const step = this.backoff
      const delay = step / 2 + Math.random() * (step / 2)
      this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS)
      vclog('socket', 'disconnected — will reconnect', {
        roomId: this.opts.roomId, peerId: this.opts.peerId, why, delayMs: Math.round(delay),
      })
      this.reconnectTimer = setTimeout(() => this.reconnect(), delay)
    }

    const abandon = () => handleClosed('unresponsive')
    this.abandonAttempt = abandon

    ws.onopen = () => {
      clearTimeout(openDeadline)
      if (this.abandonAttempt === abandon) this.abandonAttempt = null
      this.backoff = INITIAL_BACKOFF_MS
      const waiters = this.openWaiters
      this.openWaiters = []
      waiters.forEach((w) => w.resolve())
      const isReconnect = this.hasConnectedOnce
      this.hasConnectedOnce = true
      vclog('socket', isReconnect ? 'reconnected' : 'connected', { roomId: this.opts.roomId, peerId: this.opts.peerId })

      this.stopLiveness()
      this.liveness = createLivenessMonitor({
        intervalMs: SFU_PING_INTERVAL_MS,
        timeoutMs: SFU_PING_TIMEOUT_MS,
        nextRef: () => String(this.nextId++),
        sendHeartbeat: (ref) => {
          if (ws.readyState !== WebSocket.OPEN) return false
          ws.send(JSON.stringify({ id: Number(ref), method: 'ping' }))
          return true
        },
        onDead: ({ reason, waitedMs }) => {
          vclog('socket', 'SFU control socket unresponsive — replacing it', {
            roomId: this.opts.roomId, peerId: this.opts.peerId, reason, waitedMs,
          })
          handleClosed('unresponsive')
        },
      })

      this.opts.onOpen?.()
      // Notification handlers survive the reconnect (handlers map is untouched),
      // but any server state that depended on this socket being registered (push
      // delivery) was interrupted — let callers re-sync.
      if (isReconnect) this.reconnectHandlers.forEach((h) => { try { h() } catch { /* ignore */ } })
    }

    ws.onmessage = (ev) => {
      // Anything arriving proves the socket — see LivenessMonitor.heard.
      this.liveness?.heard()
      let msg: { id?: number; ok?: boolean; data?: unknown; error?: string; method?: string }
      try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') } catch { return }
      if (msg.id != null) {
        const p = this.pending.get(msg.id)
        // Not a request of ours: the answer to a ping, which `heard` has taken.
        if (!p) return
        this.pending.delete(msg.id)
        clearTimeout(p.timer)
        if (msg.ok) p.resolve(msg.data)
        else p.reject(new Error(msg.error || 'SFU request failed'))
        return
      }
      if (msg.method) {
        if (msg.method === 'hello') {
          // The server's greeting on every (re)connect: which node, which peer.
          vclog('socket', 'SFU says hello', (msg.data ?? {}) as Record<string, unknown>)
        }
        const handlers = this.handlers.get(msg.method)
        // A push nobody listens for is the shape server->client features fail
        // in: the server sends, the client silently discards, and everything
        // looks healthy. Renaming an event on one side only is not an error
        // anywhere, so say it out loud.
        if ((!handlers || handlers.size === 0) && msg.method !== 'hello' && !KNOWN_UNHANDLED_NOTIFICATIONS.has(msg.method)) {
          console.warn('[sfu-socket] no handler for server notification', msg.method)
        }
        handlers?.forEach((h) => {
          try { h(msg.data) } catch { /* ignore handler errors */ }
        })
        this.opts.onNotification?.(msg.method, msg.data)
      }
    }

    ws.onclose = (ev) => handleClosed('close', ev)

    ws.onerror = () => { /* onclose will handle reconnect */ }
  }

  /**
   * The browser says the network is back. Probe the open socket at once
   * rather than at its next ping (see SFU_ONLINE_PROBE_TIMEOUT_MS for why not
   * tighter), or — if it is already down and waiting out a backoff the outage
   * grew — reconnect now.
   */
  private handleOnline(): void {
    if (this.closedByUser) return
    const ws = this.ws
    if (ws && ws.readyState === WebSocket.OPEN && this.liveness) {
      vclog('socket', 'browser back online — probing SFU control socket', { roomId: this.opts.roomId, peerId: this.opts.peerId })
      this.liveness.probe('online', SFU_ONLINE_PROBE_TIMEOUT_MS)
    } else if (this.reconnectTimer !== null) {
      vclog('socket', 'browser back online — reconnecting SFU control socket now', { roomId: this.opts.roomId, peerId: this.opts.peerId })
      clearTimeout(this.reconnectTimer)
      this.backoff = INITIAL_BACKOFF_MS
      this.reconnect()
    } else if (ws && ws.readyState === WebSocket.CONNECTING && this.abandonAttempt) {
      // An attempt begun during the outage is most likely stuck on a lost
      // handshake, and would otherwise sit out its deadline.
      vclog('socket', 'browser back online — restarting a stalled SFU connection attempt', { roomId: this.opts.roomId, peerId: this.opts.peerId })
      this.abandonAttempt()
      if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
      this.backoff = INITIAL_BACKOFF_MS
      this.reconnect()
    }
  }

  /**
   * A reconnect — through `beforeReconnect` when the owner gave one, so the
   * Screen Share page never takes its peer back from a page that took it over
   * (contract §7b). Without one it connects at once, as the meeting always has.
   */
  private reconnect(): void {
    this.reconnectTimer = null
    if (this.closedByUser) return
    const gate = this.opts.beforeReconnect
    if (!gate) {
      this.connect()
      return
    }
    void gate().then(
      (go) => {
        if (this.closedByUser) return
        if (go) {
          this.connect()
          return
        }
        vclog('socket', 'reconnect declined — staying closed', { roomId: this.opts.roomId, peerId: this.opts.peerId })
        this.close()
      },
      // A gate that failed cannot vouch for anything: stay closed.
      () => { if (!this.closedByUser) this.close() },
    )
  }

  private stopLiveness(): void {
    this.liveness?.stop()
    this.liveness = null
  }

  private rejectPending(): void {
    for (const [, p] of this.pending) { clearTimeout(p.timer); p.reject(new Error('socket closed')) }
    this.pending.clear()
  }
}
